import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface LinkedInPromptProps {
  onComplete: () => void;
}

const LinkedInPrompt: React.FC<LinkedInPromptProps> = ({ onComplete }) => {
  const { updateUserProfile, userProfile } = useAuth();
  const [linkedinUrl, setLinkedinUrl] = useState(userProfile?.linkedInURL || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const validateLinkedInUrl = (url: string): boolean => {
    const linkedinRegex = /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
    return linkedinRegex.test(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!linkedinUrl.trim()) {
      setError('Please enter your LinkedIn URL');
      return;
    }

    if (!validateLinkedInUrl(linkedinUrl)) {
      setError('Please enter a valid LinkedIn profile URL (e.g., https://www.linkedin.com/in/yourname)');
      return;
    }

    setIsLoading(true);
    try {
      await updateUserProfile({
        linkedInURL: linkedinUrl,
        hasBeenPromptedForLinkedIn: true,
      });
      onComplete();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to save LinkedIn URL. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    setIsLoading(true);
    try {
      await updateUserProfile({ hasBeenPromptedForLinkedIn: true });
      onComplete();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to save. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ts-cream flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-14 w-14 bg-ts-navy rounded-full flex items-center justify-center mb-5">
            <svg className="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </div>
          <h1 className="font-serif text-3xl font-semibold text-ts-charcoal mb-2">
            Connect Your LinkedIn
          </h1>
          <p className="text-gray-500 text-sm">
            We use your LinkedIn profile to find network connections at target companies.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-ts-border p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="linkedin-url" className="block text-sm font-medium text-ts-charcoal mb-2">
                LinkedIn Profile URL
              </label>
              <input
                type="url"
                id="linkedin-url"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://www.linkedin.com/in/yourname"
                className="w-full px-3 py-2 border border-ts-border rounded-lg shadow-sm bg-white text-ts-charcoal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-medium text-white bg-ts-navy hover:bg-ts-charcoal focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ts-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  'Continue'
                )}
              </button>

              <button
                type="button"
                onClick={handleSkip}
                disabled={isLoading}
                className="w-full py-3 px-4 border border-ts-border rounded-lg text-sm font-medium text-gray-600 bg-white hover:bg-ts-cream focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ts-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isLoading ? 'Saving...' : 'Skip for now'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LinkedInPrompt;